// TYPES
import { AppThunk } from 'redux/type';

// BASE ACTION
import { httpAction } from 'redux/base/action';

// MODEL
import {
  AccountStatus,
  MigrationStatusResponse,
  OnboardingRole,
  LookupResponse,
  CompleteRequest,
  CompleteResponse,
  B3LookupResult,
  ServiceRecipientFormValues,
  MobileUpdateRequestRequest,
  MobileUpdateRequestResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  OnboardingSessionResponse,
  NokDetailPayload,
  NextOfKinFormValues
} from 'model';

// API
import { ApiAuth, ApiOnBoarding, ApiProfile } from 'constant';

// HTTP + AUTH SERVICE
import { httpService } from 'config/http';
import { authService } from 'config/auth';

// TYPE
import { Callback } from 'interface';

// HELPERS
import { toOnboardingClientPayload } from 'helpers/onboarding';

// APP SELECTOR
import { selectOnboardingRequired, selectAppBootstrapToken } from 'redux/app/selector';

// APP ACTION
import { setAppBootstrapToken } from 'redux/app/actions';

// USER SELECTOR
import { selectLoggedInUser } from 'redux/user/selector';

// SLICE
import { onBoardingSlice } from './slice';

// SELECTOR
import { selectSessionId, selectB3LookupResult } from './selector';

const {
  changeLoading,
  setLovedOne,
  setNric,
  setAccountStatus,
  setSuggestedRole,
  setVerified,
  setSessionId,
  setB3LookupResult,
  setMobileLast4,
  setLookupBlocked,
  setCompletedOnboard,
  setB3Snapshot,
  setLinkpassPrefill,
  reset
} = onBoardingSlice.actions;

// ----------------------------- CREATE / RESUME ONBOARDING SESSION -----------------------------------
// Called whenever the onboarding wizard needs to start/resume (accountStatus NotExisting, or
// ExistingNotLinked/ExistingLinked with completedOnboard=false). Idempotent - returns the existing
// non-terminal session for this ssoUid if one already exists. profile-service only accepts the
// bootstrap JWT (from generateAssociation's POST /auth/validate) here, so this is called with the
// Auth0-based `needAuth` mechanism disabled and the bootstrap JWT set explicitly as the bearer.

const createOnboardingSession =
  (callback?: () => void): AppThunk =>
  async (dispatch, getState) => {
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!bootstrapToken) {
      if (callback) {
        callback();
      }

      return;
    }

    dispatch(
      httpAction<unknown, unknown, { sessionId: string }>(
        {
          method: 'POST',
          url: ApiOnBoarding.Sessions,
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`,
            'Content-Type': 'application/json'
          }
        },
        async (res) => {
          dispatch(setSessionId(res.data?.sessionId));

          if (callback) {
            callback();
          }
        }
      )
    );
  };

// ----------------------------- CHECK LOGGED-IN USER ACCOUNT STATUS -----------------------------------
// Called when the Welcome screen loads (only once generateAssociation's POST /auth/validate call has
// resolved - see App.container.tsx / selectAssociationStatus). That call already tells us
// onboardingRequired (stored on appReducer by generateAssociation), so this thunk does not call
// /auth/validate itself - it just reads that state and continues the branch:
//   - onboardingRequired=true  -> AccountStatus.NotExisting (fresh user, no client_login)
//                                 -> creates the onboarding session using appReducer's bootstrapToken
//                                 (from generateAssociation)
//   - onboardingRequired=false -> check /profile/migration-status:
//       - migrationRequired=true  -> AccountStatus.ExistingNotMigrated; response also carries this
//                                    branch's own bootstrapToken + suggestedRole, stored on
//                                    appReducer/onBoardingReducer respectively
//       - migrationRequired=false -> check the logged-in user's own profileLinkingStatus:
//           - 'PENDING' -> AccountStatus.ExistingNotLinked
//           - otherwise -> AccountStatus.ExistingLinked
//     Either way, createOnboardingSession fires whenever completedOnboard=false - this can be false
//     even when migrationRequired=false, e.g. an already-linked NOK who still needs to confirm a
//     (new) loved one.

export const checkAccountStatus =
  (callback?: Callback<{ accountStatus: AccountStatus; completedOnboard: boolean }>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(changeLoading(true));

    const onboardingRequired = selectOnboardingRequired(getState());

    if (onboardingRequired) {
      dispatch(setAccountStatus(AccountStatus.NotExisting));
      dispatch(setSuggestedRole(undefined));
      dispatch(setCompletedOnboard(false));
      dispatch(createOnboardingSession());
      dispatch(changeLoading(false));

      if (callback) {
        callback({ accountStatus: AccountStatus.NotExisting, completedOnboard: false });
      }

      return;
    }

    dispatch(
      httpAction<unknown, unknown, MigrationStatusResponse>(
        {
          method: 'GET',
          url: ApiProfile.MigrationStatus,
          needAuth: true,
          hideToast: true
        },
        async (migrationRes) => {
          const { profileLinkingStatus } = selectLoggedInUser(getState());

          const accountStatus = migrationRes.data?.migrationRequired
            ? AccountStatus.ExistingNotMigrated
            : profileLinkingStatus === 'PENDING'
            ? AccountStatus.ExistingNotLinked
            : AccountStatus.ExistingLinked;

          dispatch(setAccountStatus(accountStatus));
          dispatch(setSuggestedRole(migrationRes.data?.suggestedRole));
          dispatch(setCompletedOnboard(migrationRes.data?.completedOnboard || false));

          if (migrationRes.data?.bootstrapToken) {
            dispatch(setAppBootstrapToken(migrationRes.data.bootstrapToken));
          }

          if (!migrationRes.data?.completedOnboard) {
            dispatch(createOnboardingSession());
          }

          dispatch(changeLoading(false));

          if (callback) {
            callback({
              accountStatus,
              completedOnboard: !!migrationRes.data?.completedOnboard
            });
          }
        }
      )
    );
  };

// ----------------------------- FETCH ONBOARDING SESSION -----------------------------------
// GET /onboarding/sessions/{sessionId} - fetches/resumes the full session state, including
// nokDetails.name/email/mobile (sourced from LinkPass SSO claims at bootstrap - used to prefill the
// NOK's own identity fields on the "Next of Kin details" step). NRIC fields elsewhere come back
// masked.

export const getOnboardingSession =
  (callback?: Callback<OnboardingSessionResponse | undefined>): AppThunk =>
  async (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!sessionId || !bootstrapToken) {
      if (callback) {
        callback(undefined);
      }

      return;
    }

    dispatch(changeLoading(true));

    dispatch(
      httpAction<unknown, unknown, OnboardingSessionResponse>(
        {
          method: 'GET',
          url: ApiOnBoarding.SessionDetail(sessionId),
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`
          }
        },
        async (res) => {
          dispatch(changeLoading(false));

          if (callback) {
            callback(res.data);
          }
        }
      )
    );
  };

// ----------------------------- SELECT ONBOARDING ROLE -----------------------------------
// POST /onboarding/sessions/{sessionId}/role - maps the Welcome screen's lovedOne selection onto
// the session (SELF / NOK). Idempotent for the same role; 409 if the session already has a
// different role set. Requires the session created by checkAccountStatus. Response now also carries
// linkpassPrefill - stored to prefill KeyInInformation's SelfDetailsForm / ServiceRecipientForm.

export const selectOnboardingRole =
  (role: OnboardingRole, callback?: Callback<boolean>): AppThunk =>
  async (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!sessionId || !bootstrapToken) {
      if (callback) {
        callback(false);
      }

      return;
    }

    dispatch(changeLoading(true));

    dispatch(
      httpAction<{ role: OnboardingRole }, unknown, OnboardingSessionResponse>(
        {
          method: 'POST',
          url: ApiOnBoarding.SessionRole(sessionId),
          data: { role },
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`,
            'Content-Type': 'application/json'
          }
        },
        async (res) => {
          dispatch(changeLoading(false));
          dispatch(setLinkpassPrefill(res.data?.linkpassPrefill));

          if (callback) {
            callback(res.httpCode === 200);
          }
        }
      )
    );
  };

// ----------------------------- LOOKUP B3 CLIENT BY NRIC -----------------------------------
// POST /onboarding/sessions/{sessionId}/lookup - branches server-side by session.role: SELF
// matches the LinkPass NRIC from the bootstrap-JWT claims, NOK matches the submitted nric.
// NOK-only: MATCH_LINKED + blocked means the B3 is already claimed by another NOK - session stays
// in NOK_CLIENT_LOOKUP so the caller can correct the NRIC and retry.

export const lookupOnboardingNric =
  (nric: string, callback?: Callback<LookupResponse | undefined>): AppThunk =>
  async (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!sessionId || !bootstrapToken) {
      if (callback) {
        callback(undefined);
      }

      return;
    }

    dispatch(changeLoading(true));

    dispatch(
      httpAction<{ nric: string }, unknown, LookupResponse>(
        {
          method: 'POST',
          url: ApiOnBoarding.SessionLookup(sessionId),
          data: { nric },
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`,
            'Content-Type': 'application/json'
          }
        },
        async (res) => {
          dispatch(changeLoading(false));
          dispatch(setB3LookupResult(res.data?.result));
          dispatch(setMobileLast4(res.data?.mobileLast4));
          dispatch(setLookupBlocked(res.data?.blocked));

          if (callback) {
            callback(res.data);
          }
        }
      )
    );
  };

// ----------------------------- REQUEST OTP -----------------------------------
// POST /onboarding/sessions/{sessionId}/otp/send - sends a 6-digit OTP to the B3 client's mobile
// (SELF: user's own mobile; NOK: the client's mobile, read aloud to the NOK). Only valid when
// b3LookupResult=MATCH_UNLINKED; rate-limited server-side (1 send/30s, 5 sends/hour).

export const requestOtp =
  (callback?: Callback<boolean>): AppThunk =>
  async (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!sessionId || !bootstrapToken) {
      if (callback) {
        callback(false);
      }

      return;
    }

    dispatch(changeLoading(true));

    dispatch(
      httpAction<unknown, unknown, unknown>(
        {
          method: 'POST',
          url: ApiOnBoarding.SessionOtpSend(sessionId),
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`,
            'Content-Type': 'application/json'
          }
        },
        async (res) => {
          dispatch(changeLoading(false));

          if (callback) {
            callback(res.httpCode === 200);
          }
        }
      )
    );
  };

// ----------------------------- VERIFY OTP -----------------------------------
// POST /onboarding/sessions/{sessionId}/otp/verify - max 3 attempts; 4th -> 409 OTP_EXHAUSTED.

export const verifyOtp =
  (otp: string, callback?: Callback<boolean>): AppThunk =>
  async (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!sessionId || !bootstrapToken) {
      if (callback) {
        callback(false);
      }

      return;
    }

    dispatch(changeLoading(true));

    dispatch(
      httpAction<VerifyOtpRequest, unknown, VerifyOtpResponse>(
        {
          method: 'POST',
          url: ApiOnBoarding.SessionOtpVerify(sessionId),
          data: { code: otp },
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`,
            'Content-Type': 'application/json'
          }
        },
        async (res) => {
          dispatch(changeLoading(false));

          const isVerified = res.httpCode === 200;

          dispatch(setVerified(isVerified));
          dispatch(setB3Snapshot(res.data?.b3Snapshot));

          if (callback) {
            callback(isVerified);
          }
        }
      )
    );
  };

// ----------------------------- REQUEST MOBILE NUMBER UPDATE -----------------------------------
// POST /onboarding/sessions/{sessionId}/mobile-update-request (BU-20807 / BU-20218) - from the OTP
// screen, when the user can't receive the OTP. Terminates the session (MOBILE_UPDATE_REQUESTED).

export const requestMobileUpdate =
  (newMobile: string, callback?: Callback<MobileUpdateRequestResponse | undefined>): AppThunk =>
  async (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!sessionId || !bootstrapToken) {
      if (callback) {
        callback(undefined);
      }

      return;
    }

    dispatch(changeLoading(true));

    dispatch(
      httpAction<MobileUpdateRequestRequest, unknown, MobileUpdateRequestResponse>(
        {
          method: 'POST',
          url: ApiOnBoarding.SessionMobileUpdateRequest(sessionId),
          data: { newMobile },
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`,
            'Content-Type': 'application/json'
          }
        },
        async (res) => {
          dispatch(changeLoading(false));

          if (callback) {
            callback(res.data);
          }
        }
      )
    );
  };

// ----------------------------- COMPLETE ONBOARDING -----------------------------------
// POST /onboarding/sessions/{sessionId}/complete - mints the full JWT and, for a NO_MATCH lookup,
// creates the B3 client. Body shape (confirmedClient vs newClient) depends on b3LookupResult - built
// by the caller, since only the screen knows which branch applies.

export const completeOnboarding =
  (payload: CompleteRequest, callback?: Callback<CompleteResponse | undefined>): AppThunk =>
  async (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const bootstrapToken = selectAppBootstrapToken(getState());

    if (!sessionId || !bootstrapToken) {
      if (callback) {
        callback(undefined);
      }

      return;
    }

    dispatch(changeLoading(true));

    dispatch(
      httpAction<CompleteRequest, unknown, CompleteResponse>(
        {
          method: 'POST',
          url: ApiOnBoarding.SessionComplete(sessionId),
          data: payload,
          needAuth: false,
          hideToast: true,
          headers: {
            authorization: `Bearer ${bootstrapToken}`,
            'Content-Type': 'application/json'
          }
        },
        async (res) => {
          if (res.httpCode === 200) {
            // Onboarding is done, but the gateway single-session (BU-19754) is still bound to the
            // pre-onboarding token and the Linkpass token carries no associationID yet. Re-run login
            // so the authenticated session is valid: validate (gateway backfills the LinkID
            // association), refresh the token so it now carries associationID, validate again to bind
            // the single-session to that token, then flag completedOnboard to load the profile.
            await httpService.execute({
              method: 'POST',
              url: ApiAuth.GenerateAssociation,
              needAuth: true,
              hideToast: true
            });
            await authService.generateNewToken(true);
            await httpService.execute({
              method: 'POST',
              url: ApiAuth.GenerateAssociation,
              needAuth: true,
              hideToast: true
            });
            dispatch(setCompletedOnboard(true));
          }

          dispatch(changeLoading(false));

          if (callback) {
            callback(res.data);
          }
        }
      )
    );
  };

// ----------------------------- COMPLETE SELF ONBOARDING -----------------------------------
// Shared by ConfirmDetailsMySelf (role=SELF, b3LookupResult=MATCH_UNLINKED -> confirmedClient) and
// KeyInInformation's self branch (role=SELF, b3LookupResult=NO_MATCH -> newClient). User details are
// carried in newClient/confirmedClient on the /complete call itself, not via a separate profile write.

export const completeSelfOnboarding =
  (values: ServiceRecipientFormValues, callback?: Callback<boolean>): AppThunk =>
  async (dispatch, getState) => {
    const b3LookupResult = selectB3LookupResult(getState());
    const client = toOnboardingClientPayload(values);

    const payload: CompleteRequest =
      b3LookupResult === B3LookupResult.NoMatch ? { newClient: client } : { confirmedClient: client };

    dispatch(
      completeOnboarding(payload, (result) => {
        if (callback) {
          callback(Boolean(result));
        }
      })
    );
  };

// ----------------------------- COMPLETE NOK ONBOARDING -----------------------------------
// Used by ConfirmDetailsNOK/KeyInInformation's service-recipient step (role=NOK,
// b3LookupResult=MATCH_UNLINKED -> confirmedClient / NO_MATCH -> newClient). nokDetailValues is
// whatever the caller collected on its own "Next of Kin details" step (local form state only, no
// API round-trip) - retired PATCH /nok-details used to capture this ahead of time, now it's sent
// alongside the client payload in the same /complete call. Intentionally kept as a separate thunk
// from completeSelfOnboarding (rather than shared) since the NOK completion contract may diverge
// from SELF's later.

export const completeNOKOnboarding =
  (
    nokDetailValues: NextOfKinFormValues,
    values: ServiceRecipientFormValues,
    callback?: Callback<boolean>
  ): AppThunk =>
  async (dispatch, getState) => {
    const b3LookupResult = selectB3LookupResult(getState());
    const client = toOnboardingClientPayload(values);
    const nokDetail: NokDetailPayload = {
      name: nokDetailValues.name,
      email: nokDetailValues.email,
      mobile: nokDetailValues.mobileNo,
      relationshipToClient: nokDetailValues.relationshipToClient,
      relationshipOthers: nokDetailValues.relationshipOthers,
      homeNo: nokDetailValues.homeNo,
      isDecisionMaker: nokDetailValues.isDecisionMaker
    };

    const payload: CompleteRequest = {
      ...(b3LookupResult === B3LookupResult.NoMatch ? { newClient: client } : { confirmedClient: client }),
      nokDetail
    };

    dispatch(
      completeOnboarding(payload, (result) => {
        if (callback) {
          callback(Boolean(result));
        }
      })
    );
  };

export {
  setLovedOne,
  setNric,
  setAccountStatus,
  setSuggestedRole,
  setVerified,
  setSessionId,
  setB3LookupResult,
  setMobileLast4,
  setLookupBlocked,
  setCompletedOnboard,
  setB3Snapshot,
  setLinkpassPrefill,
  changeLoading,
  reset
};
